import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTextPanelContext } from '../../context/TextPanelContext';
import { CustomButton } from '../Button/Button';
import CloseButton from '../CloseButton/CloseButton';
import { CustomInput } from '../Input/Input';
import styles from './TextPanelEditor.module.scss';
import ContainerWithScroll from '../ContainerWithScroll/ContainerWithScroll';
import { EXAMPLE_BOTTOM_TEXT, EXAMPLE_TOP_TEXT, PANEL_TYPE } from 'engage/routes/CompanySettings/components/TextPanels/constants';
import cx from 'classnames';
import Spinner from 'common/components/Spinner';

const PANEL_MAX_LENGTH = 60;

const TextPanelEditor = ({ updateVideoTexts = () => {}, miniSidebar, closeFeatures }) => {
  const {
    topText,
    bottomText,
    updateTopText,
    updateBottomText,
    disableTextPanelEditor,
    isTextPanelsChanged,
    textPanelsTemplatesList,
    currentTemplate,
    updateTemplateId,
    isLoadingList,
  } = useTextPanelContext();

  const { t } = useTranslation();

  const handleTopTextChange = e => {
    updateTopText(e.target.value);
  };

  const handleBottomTextChange = e => {
    updateBottomText(e.target.value);
  };

  useEffect(() => {
    if (miniSidebar || closeFeatures) {
      disableTextPanelEditor();
    }
  }, [miniSidebar, closeFeatures]);

  const handleSaveChanges = useCallback(() => {
    isTextPanelsChanged && updateVideoTexts(topText, bottomText, currentTemplate);
    disableTextPanelEditor();
  }, [topText, bottomText, isTextPanelsChanged, updateVideoTexts, disableTextPanelEditor, currentTemplate]);

  useEffect(() => {
    return () => disableTextPanelEditor();
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.headerContainer}>
          <h2 className={styles.editorHeader}>{t('my_video.tools.add_text_panel')}</h2>
          <CloseButton onClose={disableTextPanelEditor} />
        </div>
        <div>
          <h3 className={styles.sectionHeader}>{t('text_panel_editor.inputs_header')}</h3>
          <CustomInput
            fieldName={t('text_panel_editor.top_item')}
            type="text"
            value={topText}
            onChange={handleTopTextChange}
            maxLength={PANEL_MAX_LENGTH}
            disabled={isLoadingList}
          />
          <CustomInput
            fieldName={t('text_panel_editor.bottom_item')}
            type="text"
            value={bottomText}
            onChange={handleBottomTextChange}
            maxLength={PANEL_MAX_LENGTH}
            disabled={isLoadingList}
          />
        </div>
        {isLoadingList ? <div className={styles.loading}>
          <Spinner width={'40px'} height={'40px'} size='3px' />
        </div> : <div className={styles.stylesListWrapper}>
          <h3 className={styles.sectionHeader}>{t('text_panel_editor.styles_header')}</h3>
          <ContainerWithScroll className={styles.scrollContainer}>
              {textPanelsTemplatesList?.map(textPanel => <div key={textPanel.id} id={textPanel.id} className={cx(styles.textPanelItemContainer, { [styles.selected]: textPanel?.id === currentTemplate?.id })} onClick={() => updateTemplateId(textPanel?.id)}>
                <div style={textPanel.containerStyles} className={cx(styles.container, { [styles.twoPanels]: textPanel?.type === PANEL_TYPE.TWO_PANELS.type })}>
                  <div style={textPanel.topLabelStyles}>{EXAMPLE_TOP_TEXT}</div>
                  <div style={textPanel.bottomLabelStyles}>{EXAMPLE_BOTTOM_TEXT}</div>
              </div>
            </div>)}
          </ContainerWithScroll>
        </div>}
      </div>
      <div>
        <CustomButton
          onClick={handleSaveChanges}
          disabled={
            !isTextPanelsChanged ||
            (topText !== '' && bottomText !== '' && topText?.trim().length === 0 && bottomText?.trim().length === 0)
          }
          className={styles.saveBtn}
          label={t('general.done')}
          variant="primary"
        />
      </div>
    </div>
  );
};

export default TextPanelEditor;
